import { useQuery } from '@apollo/client/react';
import { List } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { scaleRating } from '@guuru/rating-common';
import { HighlightCard } from '@guuru/react-card';
import { LoadMore } from '@guuru/react-pagination';
import { Rating } from '@guuru/react-rating';
import RelativeTime from '../../common/RelativeTime';
import EmptyList from './EmptyList';
import GET_EXPERTS from './queries/GetPersistedExperts';

interface ExpertsListProps {
  url?: string;
  first?: number;
}

const ExpertsList = function ({ url = '', first = 20 }: ExpertsListProps) {
  const { partnerId } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { search } = useLocation();

  const { data, loading, fetchMore } = useQuery(GET_EXPERTS, {
    variables: {
      partnerId: partnerId!,
      first,
      loadMore: first > 10,
    },
  });

  const { edges: experts = [], pageInfo = {} } = data?.community?.experts || {};

  const isReady = experts.length >= 2;

  return (
    <List
      dataSource={isReady ? experts : []}
      loading={loading}
      locale={{ emptyText: <EmptyList /> }}
      grid={{
        gutter: [24, 8],
        xs: 1,
        sm: 2,
        md: 3,
        lg: 4,
        xl: 4,
        xxl: 4,
        xxxl: 4,
      }}
      loadMore={
        <LoadMore
          style={{ flex: 'none' }}
          data={{ pageInfo }}
          fetchMore={fetchMore}
          loading={loading}
          size="large"
        />
      }
      renderItem={({ node: { externalId: key, ...expert } }) => (
        <List.Item>
          <HighlightCard
            key={key}
            title={expert.name}
            avatar={expert.photoUrl ?? undefined}
            description={expert.bio}
            url={`${url}${key}${search}`}
            footer={
              <>
                {t('pages:::memberSince')}{' '}
                <RelativeTime date={expert.joinedAt} locale={language} />
              </>
            }
            extraTitle={
              <Rating rating={scaleRating(expert.rating)} hoverable={false} />
            }
          />
        </List.Item>
      )}
    />
  );
};

export default ExpertsList;
